// @ts-strict-ignore
import { schema } from './schema'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Modal } from '@components/Modal'
import { useNotifications } from '@components/NotificationsProvider'
import { Box, Button, Field, Grid, Span } from '@gassan-ui'
import { useNewsletterSignupMutation } from '@generated'
import { capitalizeWords } from '@lib/capitalize-words'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { Form, Formik } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'

type FeaturedDigitalProductModalProps = {
  title: string
  handleToggle: () => void
  handleSuccess: () => void
}

export const FeaturedDigitalProductModal: FC<FeaturedDigitalProductModalProps> = ({
  title,
  handleToggle,
  handleSuccess,
}) => {
  const { t } = useTranslation(['notifications', 'forms', 'navigation', 'other'])
  const [, submitNewsletterSignup] = useNewsletterSignupMutation()
  const notifications = useNotifications()

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open) {
          handleToggle()
        }
      }}
      defaultOpen
      title={title}
    >
      <Formik
        validationSchema={schema}
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          agree: false,
        }}
        validateOnBlur
        validateOnChange
        onSubmit={async (values) => {
          const { firstName, lastName, email } = values
          const { data, error } = await submitNewsletterSignup({
            input: {
              firstName: capitalizeWords(firstName),
              lastName: capitalizeWords(lastName),
              email,
              gassanNewsletter: true,
              choicesNewsletter: true,
            },
          })
          if (error) {
            if (process.env.NODE_ENV === 'development') {
              console.error(error)
            }
            notifications.addNotification({
              variant: 'error',
              label: t('refreshAndTryAgain', { ns: 'notifications' }),
            })
          }

          if (data) {
            notifications.addNotification({
              variant: 'simple-confirmation',
              label: t('signupCompleted', { ns: 'notifications' }),
            })
            if (['staging', 'production'].includes(process.env.NEXT_PUBLIC_STAGE)) {
              genericPushToTrackingLayer('ContactForm', 'Send')
            }
            genericPushToTrackingLayer('nieuwsbrief', 'inschrijving')
            handleSuccess()
          }
        }}
      >
        {({ isSubmitting, dirty, isValid }) => (
          <Form>
            <Grid gridTemplateColumns="1fr" gridGap={[0, '0.5rem']}>
              <Grid gridTemplateColumns={['1fr', '1fr 1fr']} gridColumnGap="1.5rem">
                <Field
                  name="firstName"
                  label={t('labels.firstName', { ns: 'forms' })}
                  field="input"
                />
                <Field
                  name="lastName"
                  label={t('labels.lastName', { ns: 'forms' })}
                  field="input"
                />
              </Grid>
              <Field name="email" label={t('labels.email', { ns: 'forms' })} field="input" />
              <Box mb={{ _: '1rem', small: '0', large: '1rem' }}>
                <Field
                  name="agree"
                  field="checkbox"
                  label={
                    <Span>
                      {t('labels.privacyNotice', { ns: 'forms' })}
                      <LocalizedLink href={t('privacyPolicy', { ns: 'navigation' })}>
                        <a
                          style={{ fontWeight: 'bold' }}
                          href={t('privacyPolicy', { ns: 'navigation' })}
                        >
                          {t('labels.privacyPolicy', { ns: 'forms' })}
                        </a>
                      </LocalizedLink>
                    </Span>
                  }
                />
              </Box>
              <Button variant="dark" type="submit" status={isSubmitting ? 'loading' : 'idle'}>
                {t('signup', { ns: 'other' })}
              </Button>
            </Grid>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
